import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * PM-tool sync client (ported Jira / Smartsheet / Azure DevOps pattern).
 * api_token is write-only — it is sent on create and never returned.
 */

export const PmConnectionSchema = z.object({
  id: z.string(),
  platform: z.enum(['jira', 'smartsheet', 'azure_devops']),
  display_name: z.string(),
  base_url: z.string(),
  auth_email: z.string().nullable(),
  scope_query: z.string().nullable(),
  status: z.string(),
  last_sync_at: z.string().nullable(),
  last_sync_status: z.string().nullable(),
  created_at: z.string(),
});
export type PmConnection = z.infer<typeof PmConnectionSchema>;

const ConnectionListSchema = z.object({ items: z.array(PmConnectionSchema) });

export function usePmConnectionsQuery(engagementId: string | undefined) {
  return useQuery({
    queryKey: ['pm-integrations', engagementId] as const,
    queryFn: () =>
      apiClient(
        `/api/v1/engagements/${encodeURIComponent(engagementId!)}/pm-integrations`,
        ConnectionListSchema,
      ),
    enabled: Boolean(engagementId),
  });
}

export interface CreatePmConnectionBody {
  platform: 'jira' | 'smartsheet' | 'azure_devops';
  display_name: string;
  base_url: string;
  auth_email?: string | null;
  api_token: string;
  scope_query?: string | null;
}

export function useCreatePmConnectionMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePmConnectionBody) =>
      apiClient(
        `/api/v1/engagements/${encodeURIComponent(engagementId!)}/pm-integrations`,
        PmConnectionSchema,
        { method: 'POST', body },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pm-integrations', engagementId] });
    },
  });
}

export function useDeletePmConnectionMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (connectionId: string) =>
      apiClient(`/api/v1/pm-integrations/${encodeURIComponent(connectionId)}`, z.unknown(), {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pm-integrations', engagementId] });
    },
  });
}

const TestResultSchema = z.object({ ok: z.boolean(), detail: z.string() });

export function useTestPmConnectionMutation() {
  return useMutation({
    mutationFn: (connectionId: string) =>
      apiClient(
        `/api/v1/pm-integrations/${encodeURIComponent(connectionId)}/test`,
        TestResultSchema,
        { method: 'POST', body: {} },
      ),
  });
}

export const PmSyncResultSchema = z.object({
  status: z.enum(['success', 'partial', 'failed']),
  items_seen: z.number(),
  items_created: z.number(),
  items_updated: z.number(),
  items_skipped: z.number(),
  error: z.string().nullable(),
});
export type PmSyncResult = z.infer<typeof PmSyncResultSchema>;

export function useRunPmSyncMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (connectionId: string) =>
      apiClient(
        `/api/v1/pm-integrations/${encodeURIComponent(connectionId)}/sync`,
        PmSyncResultSchema,
        { method: 'POST', body: {} },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pm-integrations', engagementId] });
      void qc.invalidateQueries({ queryKey: ['edl', engagementId] });
      void qc.invalidateQueries({ queryKey: ['pm-sync-logs'] });
    },
  });
}

const SyncLogSchema = z.object({
  id: z.string(),
  status: z.string(),
  items_seen: z.number(),
  items_created: z.number(),
  items_updated: z.number(),
  items_skipped: z.number(),
  error: z.string().nullable(),
  duration_ms: z.number(),
  created_at: z.string(),
});
export type PmSyncLog = z.infer<typeof SyncLogSchema>;

export function usePmSyncLogsQuery(connectionId: string | undefined) {
  return useQuery({
    queryKey: ['pm-sync-logs', connectionId] as const,
    queryFn: () =>
      apiClient(
        `/api/v1/pm-integrations/${encodeURIComponent(connectionId!)}/logs`,
        z.object({ items: z.array(SyncLogSchema) }),
      ),
    enabled: Boolean(connectionId),
  });
}
